import _ from '../../utils/underscore';
import {
    IFreePlayVoucherData,
    IFreePlayVoucherDelta
} from "../../../../common/interfaces/free-play/free-play-voucher-data.interface";


export interface IAddOrEditResponse {
    updatedValue?: IFreePlayVoucherData;
    wasAdded: boolean;
}

export class FreePlayVoucherList {
    constructor(values: IFreePlayVoucherData[] = []) {
        if (values && values.length) {
            this.reset(values);
        }
    }

    values(): IFreePlayVoucherData[] {
        return [...this._vouchers];
    }

    get length() {
        return this._vouchers.length;
    }

    reset(newValues: IFreePlayVoucherData[]): void {
        this._vouchers = [];
        newValues.forEach(item => this._vouchers.push(item));
        this._sort();
    }

    add(newValues: IFreePlayVoucherData[]): void {
        newValues.forEach(item => {
            // shallow copy voucher before saving. Avoid side effect in case the voucher object changed outside the service
            const voucher: IFreePlayVoucherData = Object.assign({}, item);
            this._vouchers.push(voucher);
        });
        this._sort();
    }

    update(delta: IFreePlayVoucherDelta): IFreePlayVoucherData | null {
        const updated = this._updateById(delta);
        if (updated) {
            this._sort();
        }
        return updated;
    }

    addOrUpdate(newValue: IFreePlayVoucherDelta | IFreePlayVoucherData): IAddOrEditResponse {
        const updated = this._updateById(newValue);
        if (!updated) {
            this.add([newValue as IFreePlayVoucherData]);
            return { updatedValue: null, wasAdded: true };
        } else {
            this._sort();
            return { updatedValue: updated, wasAdded: false };
        }
    }

    remove(ID: string): IFreePlayVoucherData | null {
        const index = this._findIndexByID(ID);
        if (index < 0) {
            return null;
        }

        const toDeleteEntry = this._vouchers[index];
        this._vouchers.splice(index, 1);
        return toDeleteEntry;
    }

    getByID(voucherID: string): IFreePlayVoucherData | null {
        const index = this._findIndexByID(voucherID);
        if (index < 0) {
            return null;
        }

        const voucher = this._vouchers[index];
        return voucher;
    }

    static getMergedResult(initial: IFreePlayVoucherData, delta: IFreePlayVoucherDelta): IFreePlayVoucherData | null {
        if (initial.ID !== delta.ID) {
            return null;
        }

        const merged = {...initial};
        for (const prop in initial) {
            const updatedValue = delta[prop];
            // nulls are cosidered "good" override values
            if (_.isUndefined(updatedValue)) {
                continue;
            }

            merged[prop] = updatedValue;
        }
        return merged;
    }

    private _findIndexByID(voucherID: string): number {
        for (let index = 0; index < this._vouchers.length; index++) {
            const voucher = this._vouchers[index];
            if (voucher.ID === voucherID) {
                return index;
            }
        }
        return -1;
    }
    
    private _updateById(item: IFreePlayVoucherDelta): IFreePlayVoucherData | null {
        const index = this._findIndexByID(item.ID);
        if (index < 0) {
            return null;
        }

        let updated = false;
        const oldVoucher = this._vouchers[index];

        // shallow clone and update changed values
        const updatedVoucher = {...oldVoucher};
        for (const prop in item) {
            const updatedValue = item[prop];
            // nulls are considered "good" override values
            if (_.isUndefined(updatedValue)) {
                continue;
            }

            updated = true;
            updatedVoucher[prop] = updatedValue;
        }
        
        // put new object (if it's the case)
        if (updated) {
            this._vouchers[index] = updatedVoucher;
        }

        // old strategy was kind of:
        // const updatedVoucherRelevantValues = _.cleanObject(voucher, undefined) as IFreePlayUpdateVoucher;
        // const updatedVoucher = Object.assign(oldVoucher, item);
        // this._vouchers[index] = updatedVoucher;

        return updated? this._vouchers[index] : undefined;
    }

    appendVouchers(toAdd: IFreePlayVoucherData[]): FreePlayVoucherList {
        const res = new FreePlayVoucherList(this.values());
        res.add(toAdd);
        return res;
    }

    private _sort(): void {
        this._vouchers.sort((v1, v2) => {
            if (_.isNullOrUndefined(v1.ExpirationDate)) {
                if (_.isNullOrUndefined(v2.ExpirationDate)) {
                    return 0;
                } else {
                    return 1;
                }
            }
            return new Date(v1.ExpirationDate).getTime() < new Date(v2.ExpirationDate).getTime() ? -1 : 1;
        });

    }

    private _vouchers: IFreePlayVoucherData[] = [];
}
